import { useEffect, useState, useCallback } from "react";
import { Send, Mail, Palette } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";

const STATUS_STYLE = {
  SENT: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
  SIMULATED: "bg-[#E0E7FF] text-[#3730A3] border-[#C7D2FE]",
  FAILED: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

function ProjectRecipientsRow({ project, onSaved }) {
  const { t } = useLang();
  const [emails, setEmails] = useState((project.report_recipients || []).join(", "));
  const [enabled, setEnabled] = useState(project.digest_enabled !== false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/projects/${project.id}/recipients`, {
        report_recipients: emails.split(",").map((e) => e.trim()).filter(Boolean),
        digest_enabled: enabled,
      });
      toast.success(`${project.key} ✓`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/digest/send-now", { project_id: project.id });
      const r = data.results[0];
      if (r?.status === "FAILED") toast.error(`${project.key}: ${r.error}`);
      else toast.success(`${project.key}: ${r?.status}`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded-md p-3 flex flex-wrap items-center gap-3" data-testid={`digest-project-${project.key}`}>
      <div className="min-w-[120px]">
        <div className="text-sm font-medium">{project.key}</div>
        <div className="text-[11px] text-muted-foreground">{project.pm_name}</div>
      </div>
      <Input className="flex-1 min-w-[240px] h-8 text-sm" data-testid={`recipients-input-${project.key}`}
        value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="pm@company.com, lead@company.com" />
      <div className="flex items-center gap-1.5">
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid={`digest-enabled-${project.key}`} />
        <span className="text-xs text-muted-foreground">{t("digestEnabled")}</span>
      </div>
      <Button size="sm" variant="outline" className="h-8" disabled={busy}
        data-testid={`recipients-save-${project.key}`} onClick={save}>{t("saveSettings")}</Button>
      <Button size="sm" className="h-8 bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy}
        data-testid={`digest-send-now-${project.key}`} onClick={sendNow}>
        <Send className="h-3 w-3 mr-1.5" /> {t("sendNow")}
      </Button>
    </div>
  );
}

function ProviderFields({ settings, set }) {
  const { t } = useLang();
  return (
    <>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">{t("providerLabel")}</Label>
          <Select value={settings.provider} onValueChange={(v) => set("provider", v)}>
            <SelectTrigger data-testid="email-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NONE">{t("simulatedOutbox")}</SelectItem>
              <SelectItem value="SENDGRID">SendGrid</SelectItem>
              <SelectItem value="SMTP">SMTP</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("fromEmail")}</Label>
          <Input data-testid="email-from-input" value={settings.from_email || ""}
            onChange={(e) => set("from_email", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("fromName")}</Label>
          <Input value={settings.from_name || ""} onChange={(e) => set("from_name", e.target.value)} />
        </div>
      </div>
      {settings.provider === "SENDGRID" && (
        <div className="space-y-1 max-w-md">
          <Label className="text-xs">SendGrid API Key {settings.has_sendgrid_key && "(saved — leave blank to keep)"}</Label>
          <Input type="password" data-testid="sendgrid-key-input"
            placeholder={settings.has_sendgrid_key ? "••••••••" : "SG.xxxxx"}
            onChange={(e) => set("sendgrid_api_key", e.target.value)} />
        </div>
      )}
      {settings.provider === "SMTP" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">SMTP Host</Label>
            <Input data-testid="smtp-host-input" value={settings.smtp_host || ""}
              onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.office365.com" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Port</Label>
            <Input type="number" value={settings.smtp_port || 587}
              onChange={(e) => set("smtp_port", Number(e.target.value))} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Username</Label>
            <Input value={settings.smtp_username || ""} onChange={(e) => set("smtp_username", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Password {settings.has_smtp_password && "(saved — leave blank to keep)"}</Label>
            <Input type="password" placeholder={settings.has_smtp_password ? "••••••••" : ""}
              onChange={(e) => set("smtp_password", e.target.value)} />
          </div>
          <div className="flex items-center gap-2 pt-4">
            <Switch checked={settings.smtp_use_tls !== false} onCheckedChange={(v) => set("smtp_use_tls", v)} />
            <span className="text-xs">STARTTLS</span>
          </div>
        </div>
      )}
    </>
  );
}

function ProviderCard({ settings, set, busy, onSave, onTestSend, testTo, setTestTo }) {
  const { t } = useLang();
  return (
    <div className="apha-card p-5 space-y-4">
      <h3 className="font-heading font-semibold flex items-center gap-2">
        <Mail className="h-4 w-4 text-[#1E3A8A]" /> {t("providerLabel")}
      </h3>
      <ProviderFields settings={settings} set={set} />
      <div className="flex flex-wrap items-end gap-3 pt-2 border-t">
        <div className="space-y-1">
          <Label className="text-xs">{t("digestTime")}</Label>
          <Input type="time" data-testid="digest-time-input" className="w-32"
            value={settings.digest_time || "10:30"} onChange={(e) => set("digest_time", e.target.value)} />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch checked={settings.digest_enabled !== false} data-testid="digest-global-switch"
            onCheckedChange={(v) => set("digest_enabled", v)} />
          <span className="text-sm">{t("digestEnabled")}</span>
        </div>
        <Button data-testid="email-settings-save-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]"
          disabled={busy} onClick={onSave}>{t("saveSettings")}</Button>
        <div className="flex items-end gap-2 ml-auto">
          <div className="space-y-1">
            <Label className="text-xs">{t("testSend")}</Label>
            <Input data-testid="test-email-input" className="w-56" placeholder="you@company.com"
              value={testTo} onChange={(e) => setTestTo(e.target.value)} />
          </div>
          <Button variant="outline" data-testid="test-email-btn" disabled={busy || !testTo} onClick={onTestSend}>
            <Send className="h-3.5 w-3.5 mr-1.5" /> {t("testSend")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BrandingCard({ settings, set, busy, onSave }) {
  const { t } = useLang();
  return (
    <div className="apha-card p-5 space-y-4" data-testid="digest-branding-card">
      <h3 className="font-heading font-semibold flex items-center gap-2">
        <Palette className="h-4 w-4 text-[#1E3A8A]" /> {t("digestBranding")}
      </h3>
      <p className="text-xs text-muted-foreground">{t("brandingHint")}</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">{t("companyName")}</Label>
          <Input data-testid="brand-company-input" value={settings.brand_company_name || ""}
            placeholder="Soluix" onChange={(e) => set("brand_company_name", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("logoUrl")}</Label>
          <Input data-testid="brand-logo-input" value={settings.brand_logo_url || ""}
            placeholder="https://company.com/logo.png" onChange={(e) => set("brand_logo_url", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("accentColor")}</Label>
          <div className="flex gap-2">
            <input type="color" data-testid="brand-color-picker"
              className="h-9 w-12 rounded-md border cursor-pointer p-0.5"
              value={settings.brand_accent_color || "#1E3A8A"}
              onChange={(e) => set("brand_accent_color", e.target.value)} />
            <Input data-testid="brand-color-input" className="w-28 font-mono text-sm"
              value={settings.brand_accent_color || "#1E3A8A"}
              onChange={(e) => set("brand_accent_color", e.target.value)} />
          </div>
        </div>
      </div>
      <div>
        <Label className="text-xs">{t("brandingPreview")}</Label>
        <div className="mt-1.5 rounded-lg p-4 text-white max-w-md" data-testid="brand-preview"
          style={{ background: /^#[0-9a-fA-F]{6}$/.test(settings.brand_accent_color || "") ? settings.brand_accent_color : "#1E3A8A" }}>
          {settings.brand_logo_url && (
            <img src={settings.brand_logo_url} alt="logo" className="h-9 max-w-[200px] object-contain mb-2"
              onError={(e) => { e.target.style.display = "none"; }} />
          )}
          <div className="text-[10px] tracking-[2px] opacity-80">
            {(settings.brand_company_name || "APHA").toUpperCase()} · DAILY SCRUM DIGEST
          </div>
          <div className="font-bold mt-0.5 text-sm">BSS-EMS — Exposure Management System</div>
        </div>
      </div>
      <Button data-testid="brand-save-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]"
        disabled={busy} onClick={onSave}>{t("saveSettings")}</Button>
    </div>
  );
}

function DeliveryLog({ deliveries }) {
  const { t } = useLang();
  return (
    <div className="apha-card overflow-x-auto" data-testid="email-delivery-log">
      <div className="px-4 pt-4 font-heading font-semibold">{t("deliveryLog")}</div>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Time</TableHead><TableHead>{t("recipientsLabel")}</TableHead>
          <TableHead>Subject</TableHead><TableHead>{t("statusLabel")}</TableHead>
          <TableHead>Provider</TableHead><TableHead>Error</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {(deliveries || []).map((d) => (
            <TableRow key={d.id}>
              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                {new Date(d.created_at).toLocaleString()}
              </TableCell>
              <TableCell className="text-xs">{(d.to || []).join(", ")}</TableCell>
              <TableCell className="text-xs">{d.subject}</TableCell>
              <TableCell>
                <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${STATUS_STYLE[d.status] || ""}`}>
                  {d.status}
                </span>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">{d.provider}</TableCell>
              <TableCell className="text-[11px] text-[#991B1B] max-w-[220px] truncate" title={d.error || ""}>
                {d.error || "—"}
              </TableCell>
            </TableRow>
          ))}
          {(deliveries || []).length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground">{t("noData")}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default function AdminEmail() {
  const { t } = useLang();
  const [settings, setSettings] = useState(null);
  const [projects, setProjects] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/email-settings").then((r) => setSettings(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/admin/email-deliveries").then((r) => setDeliveries(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!settings || !projects) return <Skeleton className="h-60 w-full" />;

  const set = (k, v) => setSettings({ ...settings, [k]: v });

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...settings };
      delete body.has_sendgrid_key;
      delete body.has_smtp_password;
      delete body.updated_at;
      delete body.updated_by;
      const { data } = await api.put("/admin/email-settings", body);
      setSettings(data);
      toast.success(t("saveSettings") + " ✓");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const testSend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/email-settings/test", { to: testTo });
      if (data.status === "FAILED") toast.error(data.error);
      else toast.success(`${t("testSend")}: ${data.status}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-email-tab">
      <ProviderCard settings={settings} set={set} busy={busy} onSave={save}
        onTestSend={testSend} testTo={testTo} setTestTo={setTestTo} />
      <BrandingCard settings={settings} set={set} busy={busy} onSave={save} />
      <div className="apha-card p-5 space-y-3">
        <h3 className="font-heading font-semibold">{t("digestSchedule")}</h3>
        <p className="text-xs text-muted-foreground">{t("digestIntro")}</p>
        <div className="space-y-2">
          {projects.filter((p) => p.status === "ACTIVE").map((p) => (
            <ProjectRecipientsRow key={p.id} project={p} onSaved={load} />
          ))}
        </div>
      </div>
      <DeliveryLog deliveries={deliveries} />
    </div>
  );
}
